import { Order, Driver, SystemConfig } from '../../../types';
import { DistanceService } from '../core/distanceService';
import { COLD_START_MINS_PER_KM } from '../constants';

/**
 * Calcula o pulso operacional do Hub baseado na capacidade nominal configurada.
 */
export const calculateSystemPulse = (
    orders: Order[],
    drivers: Driver[],
    config: SystemConfig
): { activeDrivers: number, pendingCount: number, loadFactor: number, oracleMultiplier: number, capacityThreshold: number, isBusy: boolean } => {
    const activeDrivers = drivers.filter(d => d.status === 'active').length || 1;
    const operationalBacklog = orders.filter(o => ['confirmed', 'scheduled_confirmed', 'ready'].includes(o.status)).length;
    const pendingCount = operationalBacklog > 0 ? operationalBacklog : orders.filter(o => o.status === 'pending').length;

    const loadFactor = pendingCount / activeDrivers;

    // Threshold baseado na bag da frota
    const capacityThreshold = config.fleetConfig.maxOrdersPerRoute || 4;

    const now = new Date();
    const day = now.getDay();
    const hour = now.getHours();

    let oracleMultiplier = 1.0;
    if (config.oracle?.active) {
        const dayWeight = config.oracle.dailyWeights[day] || 1.0;
        const nextHour = (hour + 1) % 24;
        const nextHourShare = config.oracle.hourlyShare[nextHour] || (1 / 24);
        oracleMultiplier = 1 + (((dayWeight * (nextHourShare / (1 / 24))) - 1) * 0.3);
        oracleMultiplier = Math.max(0.7, Math.min(1.5, oracleMultiplier));
    }

    const isBusy = loadFactor > (capacityThreshold * 0.8);

    return { activeDrivers, pendingCount, loadFactor, oracleMultiplier, capacityThreshold, isBusy };
};

/**
 * DISPONIBILIDADE PREDITIVA (E.T.A. Retorno)
 * V2025.02.08: Correção Completa de Disponibilidade
 * - Disponibilidade Presumida: motoboy sem pickupConfirmed está disponível para mudanças
 * - Disponibilidade Preditiva: calcula retorno real de quem está na rua
 * - Usa driver.lat/lng (atualizado pelo GPS) em vez de currentLat/currentLng
 */
export const calculateDriverMinutesToHub = (
    driver: Driver,
    driverOrders: Order[],
    hubLat: number,
    hubLng: number,
    serviceTimePerOrder: number = 3
): number => {
    // REGRA DE DISPONIBILIDADE PRESUMIDA:
    // Se o motorista não tem pedidos ATIVOS (assigned/dispatched) ou todos estão sem pickupConfirmed,
    // ele está disponível (retorno = 0)
    const trulyActiveOrders = driverOrders.filter(o => 
        (o.status === 'assigned' || o.status === 'dispatched') && 
        o.pickupConfirmed === true
    );
    
    // Se não tem pedidos realmente ativos (com pickup confirmado), está disponível
    if (trulyActiveOrders.length === 0) return 0;

    // v6.1: Prioriza Métricas Reais do Piloto para Service Time (Tempo de porta)
    const effectiveServiceTime = driver.avgServiceTimeMinutes || serviceTimePerOrder || 3;

    // 1. Definição do Ponto de Partida
    // Usa driver.lat/lng que são atualizados pelo GPS em tempo real
    // Fallback para hub se não tiver coordenadas válidas
    let currentLat = (driver.lat && driver.lat !== 0) ? driver.lat : hubLat;
    let currentLng = (driver.lng && driver.lng !== 0) ? driver.lng : hubLng;

    let totalTravelMins = 0;
    let totalServiceMins = 0;

    // 2. Simulação da Rota Pendente (Execução)
    if (trulyActiveOrders.length > 0) {
        const sorted = [...trulyActiveOrders].sort((a, b) => (a.routeSequence || 99) - (b.routeSequence || 99));

        sorted.forEach((o, idx) => {
            if (o.lat && o.lng) {
                // Se o pedido já tem duração estimada (Google), usamos ela. Senão, heurística.
                let legMins = 0;

                // Para o primeiro item, a distância é de onde o motorista está agora até ele
                const dist = DistanceService.getDistanceSync(currentLat, currentLng, o.lat, o.lng);

                // Se temos estimativa gravada e é o primeiro da fila, tentamos usar, mas ponderamos pela distância real atual
                // para evitar que um motorista longe do hub "herde" um tempo curto pré-calculado
                if (idx === 0 && o.estimatedDuration) {
                    // Ajuste dinâmico: se a distância atual for muito diferente da original, recalcula
                    // v56.0: Centralized Time Logic
                    if (o.estimatedDuration && dist < 0.2) { // Só confia no estimado se estiver "agora" no ponto
                        legMins = o.estimatedDuration;
                    } else {
                        legMins = DistanceService.getTravelTime(dist, 'own', driver);
                    }
                } else {
                    legMins = DistanceService.getTravelTime(dist, 'own', driver);
                }

                totalTravelMins += legMins;
                totalServiceMins += effectiveServiceTime;

                // Avança o cursor para o próximo ponto
                currentLat = o.lat;
                currentLng = o.lng;
            }
        });
    }

    // 3. Perna de Retorno (Backhaul)
    // Agora 'currentLat/Lng' apontam para o último cliente da rota (se houver pedidos) ou para o motorista (se vazio).
    // Isso garante que assim que despacha, o sistema calcula (Hub->Cliente) + (Cliente->Hub).
    const distFinalToHub = DistanceService.getDistanceSync(currentLat, currentLng, hubLat, hubLng);
    const returnLegMins = DistanceService.getTravelTime(distFinalToHub, 'own', driver);

    totalTravelMins += returnLegMins;

    return Math.ceil(totalTravelMins + totalServiceMins);
};
